import {Fragment, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {notification} from "antd";
import ActionBar from "~/layout/ActionBar";
import {Button, Loading, Modal, Icon} from "~/components";
import {
	trainingScheduleActions,
	trainingScheduleErrorSelector,
	trainingScheduleFilterSelector,
	trainingScheduleItemsSelector,
	trainingScheduleLoadingSelector,
	trainingSchedulePaginationSelector,
} from "../trainingScheduleSlice";
import {apiError, handleRequest} from "~/utils";
import {trainingScheduleApi, authApi} from "~/api";
import {useCan} from "~/hooks";
import {authActions, currentUserSelector} from "~/features/Auth/authSlice";
import {TrainingScheduleTable, TrainingScheduleFormAddEdit, TrainingScheduleSearchBar, TrainingScheduleDetail} from "../components";

const STATUS_LABELS = {
	trained:     'Xác nhận đã đào tạo',
	not_trained: 'Xác nhận chưa đào tạo',
	postponed:   'Đời lịch',
	cancelled:   'Hủy đào tạo',
	upcoming:    'Khôi phục lịch',
};

function TrainingSchedule() {

	const dispatch     = useDispatch();
	const items        = useSelector(trainingScheduleItemsSelector);
	const loading      = useSelector(trainingScheduleLoadingSelector);
	const error        = useSelector(trainingScheduleErrorSelector);
	const pagination   = useSelector(trainingSchedulePaginationSelector);
	const filter       = useSelector(trainingScheduleFilterSelector);
	const currentUser  = useSelector(currentUserSelector);

	const canAdd          = useCan('trainingScheduleAdd');
	const canEdit         = useCan('trainingScheduleEdit');
	const canDelete       = useCan('trainingScheduleDelete');
	const canChangeStatus = useCan('trainingScheduleStatus');
	const canPostpone     = useCan('trainingSchedulePostpone');
	const canCancel       = useCan('trainingScheduleCancel');

	// Chỉ nhân sự và trưởng khu vực mới được sửa/dời/hủy
	const MANAGER_ROLES   = ['personnel', 'personnelManager', 'salesSupper'];
	const isManager       = MANAGER_ROLES.includes(currentUser?.role?.key);

	const [itemEdit, setItemEdit] = useState({});
	const [openModal, setOpenModal] = useState({addEdit: false, delete: false, detail: false});
	const [detailItem, setDetailItem] = useState({});

	const [searchParams, setSearchParams] = useSearchParams();

	const handleModalOpen  = (modal) => setOpenModal(prev => ({...prev, [modal]: true}));
	const handleModalClose = (modal) => setOpenModal(prev => ({...prev, [modal]: false}));

	useEffect(() => {
		dispatch(trainingScheduleActions.fetchData({...filter}));
	}, [filter]);

	// Mở chi tiết khi truy cập từ thông báo: /training-schedule?id=<id>
	useEffect(() => {
		const id = searchParams.get('id');
		if (!id) return;
		(async () => {
			let [err, response] = await handleRequest(trainingScheduleApi.detail(id));
			let message = apiError('Không tải được chi tiết lịch đào tạo', err, response);
			if (!message) {
				setDetailItem(response.data);
				handleModalOpen('detail');
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	const handleDetailClose = () => {
		handleModalClose('detail');
		if (searchParams.get('id')) {
			searchParams.delete('id');
			setSearchParams(searchParams, {replace: true});
		}
	};

	const handleReLoading = () => {
		dispatch(trainingScheduleActions.fetchData({...filter}));
	};

	if (error) {
		notification.error({message: 'Lỗi', description: error});
	}

	const handleCreate = async (data) => {
		let [err, response] = await handleRequest(trainingScheduleApi.add(data));
		let message = apiError('Tạo lịch đào tạo thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Tạo lịch đào tạo thành công'});
			dispatch(trainingScheduleActions.add(response.data));
			handleModalClose('addEdit');
		}
	};

	const handleUpdate = async (data, item) => {
		data.id = item.id;
		let [err, response] = await handleRequest(trainingScheduleApi.update(data));
		let message = apiError('Cập nhật lịch đào tạo thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Cập nhật lịch đào tạo thành công'});
			dispatch(trainingScheduleActions.update(response.data));
			handleModalClose('addEdit');
		}
	};

	const handleDelete = async () => {
		if (!itemEdit?.id) return;
		let [err, response] = await handleRequest(trainingScheduleApi.delete(itemEdit.id));
		let message = apiError('Xóa lịch đào tạo thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Xóa lịch đào tạo thành công'});
			dispatch(trainingScheduleActions.delete(itemEdit.id));
			handleModalClose('delete');
		}
	};

	const handleStatusChange = async (item, status) => {
		let [err, response] = await handleRequest(trainingScheduleApi.status({id: item.id, status}));
		const label = STATUS_LABELS[status] || status;
		let message = apiError(`${label} thất bại`, err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: `${label} thành công`});
			dispatch(trainingScheduleActions.update(response.data));

			// Cập nhật kết quả buổi quá giờ → làm mới trạng thái khóa (tự mở khóa nếu hết buổi quá giờ)
			let [errCurrent, responseCurrent] = await handleRequest(authApi.current());
			if (!errCurrent && responseCurrent?.data) {
				dispatch(authActions.loginSuccess(responseCurrent.data));
			}
		}
	};

	// Keyword → debounce; các filter khác → immediate
	const handleSearchChange = (newFilter) => {
		dispatch(trainingScheduleActions.setFilterWithDebounce(newFilter));
	};

	const handleFilterChange = (newFilter) => {
		dispatch(trainingScheduleActions.setFilter(newFilter));
	};

	const handlePaginationChange = (page) => {
		dispatch(trainingScheduleActions.setFilter({...filter, page}));
	};

	return (
		<Fragment>
			<ActionBar title="Lịch đào tạo">
				{canAdd && (
					<Button outline leftIcon={Icon.plusCircle} onClick={() => { setItemEdit({}); handleModalOpen('addEdit'); }}>
						Thêm mới
					</Button>
				)}
				<Button outline onClick={handleReLoading}>{Icon.reload}</Button>
			</ActionBar>

			<TrainingScheduleSearchBar
				filter={filter}
				onSearchChange={handleSearchChange}
				onChange={handleFilterChange}
			/>

			<div className="container">
				<div className="content">
					{loading && <Loading />}
					{items && (
						<TrainingScheduleTable
							items={items}
							pagination={pagination}
							onPaginationChange={handlePaginationChange}
							setItemEdit={setItemEdit}
							openModal={handleModalOpen}
							onStatusChange={handleStatusChange}
							canEdit={canEdit && isManager}
							canDelete={canDelete && isManager}
							canChangeStatus={canChangeStatus && isManager}
							canPostpone={canPostpone}
							canCancel={canCancel}
							currentUserId={currentUser?.id}
						/>
					)}
				</div>

				<Modal
					title="Chi tiết lịch đào tạo"
					visible={openModal.detail}
					onCancel={handleDetailClose}
					size="xl"
				>
					<TrainingScheduleDetail item={detailItem} />
				</Modal>

				<Modal
					title={itemEdit?.id ? 'Sửa lịch đào tạo' : 'Thêm lịch đào tạo'}
					visible={openModal.addEdit}
					onCancel={() => handleModalClose('addEdit')}
					size="xl"
				>
					<TrainingScheduleFormAddEdit
						item={itemEdit}
						onHandleSubmit={itemEdit?.id ? handleUpdate : handleCreate}
					/>
				</Modal>

				{itemEdit?.id && (
					<Modal
						title="Xóa lịch đào tạo"
						visible={openModal.delete}
						onCancel={() => handleModalClose('delete')}
					>
						<p className="mt-2">
							Bạn muốn xóa lịch đào tạo <b>{itemEdit?.topic}</b>?
						</p>
						<div className="d-flex justify-content-end gap modal-bottom pd-2">
							<Button white leftIcon={Icon.close} onClick={() => handleModalClose('delete')}>Đóng</Button>
							<Button primary leftIcon={Icon.delete} onClick={handleDelete}>Xóa</Button>
						</div>
					</Modal>
				)}
			</div>
		</Fragment>
	);
}

export default TrainingSchedule;
